import { useEffect, useState } from "react";
import { User, Lock, Save } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

/**
 * Profile — self-service account page.
 *
 * Lets any signed-in user view their own profile, change their display
 * name and rotate their password. Never displays or logs the password.
 */
export default function Profile() {
    const { t } = useTranslation();
    const { user } = useApp();
    const [name, setName] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

    useEffect(() => { if (user) setName(user.name || ""); }, [user]);
    if (!user) return null;

    const initials = (user.name || user.email || "?").trim()
        .split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

    const saveName = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) { toast.error(t("profile.nameEmpty", "Name cannot be empty")); return; }
        if (trimmed === user.name) return;
        setSavingName(true);
        try {
            await api.patch("/auth/me", { name: trimmed });
            toast.success(t("profile.nameUpdated", "Name updated"));
            // Force a re-fetch so the sidebar/header pick it up.
            window.location.reload();
        } catch (err) {
            toast.error(err.response?.data?.detail != null ? apiErrorMessage(err) : t("profile.couldNotSave", "Could not save"));
        } finally { setSavingName(false); }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (!pw.current || !pw.next) {
            toast.error(t("profile.fillEveryField", "Fill in every field")); return;
        }
        if (pw.next.length < 8) {
            toast.error(t("profile.passwordMin", "New password must be at least 8 characters")); return;
        }
        if (pw.next !== pw.confirm) {
            toast.error(t("profile.passwordsMismatch", "Passwords do not match")); return;
        }
        setSavingPassword(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pw.current,
                new_password: pw.next,
                confirm_password: pw.confirm,
            });
            toast.success(t("profile.passwordUpdated", "Password updated"));
            setPw({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail != null ? apiErrorMessage(err) : t("profile.couldNotChangePassword", "Could not change password"));
        } finally { setSavingPassword(false); }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8" data-testid="profile-page">
            <PageHeader
                title={t("profile.title", "My account")}
                description={t("profile.description", "Manage your personal details and sign-in credentials.")}
            />

            <section className="rounded-xl border border-neutral-200 bg-white p-6">
                <div className="flex items-start gap-4">
                    <div className="h-16 w-16 rounded-full bg-neutral-900 text-white grid place-items-center font-semibold text-lg"
                         data-testid="profile-avatar">
                        {initials || <User className="h-6 w-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{t("profile.signedInAs", "Signed in as")}</div>
                        <div className="text-sm font-mono text-neutral-900" data-testid="profile-email">{user.email}</div>
                        <div className="mt-1 text-xs text-neutral-500">
                            {t("profile.role", "Role")} · <span className="capitalize">{t(`roles.${user.profile_role || user.role}`, (user.profile_role || user.role || "").replace("_", " "))}</span>
                        </div>
                    </div>
                </div>
            </section>

            <form onSubmit={saveName} className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4"
                  data-testid="profile-name-form">
                <div>
                    <h2 className="font-semibold text-neutral-900 flex items-center gap-2">
                        <User className="h-4 w-4" /> {t("profile.displayName", "Display name")}
                    </h2>
                    <p className="text-xs text-neutral-500 mt-0.5">{t("profile.displayNameHint", "Shown throughout MiseOne.")}</p>
                </div>
                <input
                    data-testid="profile-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-11 px-3 rounded-md border border-neutral-300 bg-white text-sm"
                    placeholder={t("profile.namePlaceholder", "Your full name")}
                />
                <div>
                    <button type="submit" disabled={savingName || name.trim() === user.name}
                            data-testid="profile-name-save"
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                        <Save className="h-4 w-4" /> {savingName ? t("common.saving", "Saving…") : t("profile.saveName", "Save name")}
                    </button>
                </div>
            </form>

            <form onSubmit={savePassword} className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4"
                  data-testid="profile-password-form">
                <div>
                    <h2 className="font-semibold text-neutral-900 flex items-center gap-2">
                        <Lock className="h-4 w-4" /> {t("profile.changePassword", "Change password")}
                    </h2>
                    <p className="text-xs text-neutral-500 mt-0.5">
                        {t("profile.passwordHint", "Minimum 8 characters. You'll stay signed in on this device.")}
                    </p>
                </div>
                {[
                    { key: "current", label: t("profile.currentPassword", "Current password"), autoComplete: "current-password" },
                    { key: "next", label: t("profile.newPassword", "New password"), autoComplete: "new-password" },
                    { key: "confirm", label: t("profile.confirmPassword", "Confirm new password"), autoComplete: "new-password" },
                ].map(({ key, label, autoComplete }) => (
                    <div key={key}>
                        <label className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">
                            {label}
                        </label>
                        <input
                            type="password"
                            autoComplete={autoComplete}
                            data-testid={`profile-password-${key}`}
                            value={pw[key]}
                            onChange={(e) => setPw({ ...pw, [key]: e.target.value })}
                            className="mt-1 w-full h-11 px-3 rounded-md border border-neutral-300 bg-white text-sm"
                        />
                    </div>
                ))}
                <div>
                    <button type="submit" disabled={savingPassword}
                            data-testid="profile-password-save"
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                        <Save className="h-4 w-4" /> {savingPassword ? t("common.saving", "Saving…") : t("profile.changePassword", "Change password")}
                    </button>
                </div>
            </form>
        </div>
    );
}
